// Libraries
import { Box, Card, Flex, Text, VStack } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { useSession } from 'next-auth/react';
import { useRouter } from 'next/router';
import { useCallback, useMemo } from 'react';
// Components
import { Spinner } from '@atoms/index';
import { DataStateWrapper, Voyage, VoyageInnerList } from '@molecules/index';
import { VoyageCarousel } from '@organisms/index';
// Controllers
import { voyagesController } from '@controllers/index';
// Utils
import { withScroll } from '@/src/theme/hoc';
import { CanGroup, groupBy } from '@/src/utils/helpers/groupBy';
import text from '@translations/en.json';
// Types
import { AppPath, CacheKeys } from '@utils/index';
import { VoyageListProps } from './VoyageList.type';

/**
 * The VStack chakra ui component with a styled scroll bar.
 */
const VStackWithScroll = withScroll(VStack) as typeof VStack;

/**
 * VoyageList component.
 *
 * @component
 * @param {VoyageListProps} props - The props for the VoyageList component.
 * @param {Voyage[]} props.voyages - The array of voyages to be rendered.
 * @param {LayoutProps.h} [props.height='auto'] - The height of the VoyageList component as a string.
 * @param {Function} props.handleClick - The click event handler for selecting a voyage.
 * @param {string} props.selectedVoyageId - The ID of the currently selected voyage.
 * @returns {ReactElement} The rendered VoyageList component.
 */
export const VoyageList = (props: VoyageListProps) => {
  const { height = 'auto', carousel = false } = props;

  const sessionData = useSession();
  const router = useRouter();

  const userSub = sessionData.data?.user?.sub;
  const imo = router.query.imo as string;
  const selectedVoyageId = router.query.voyage as string;

  const voyagesQuery = useQuery({
    queryKey: [CacheKeys.VOYAGES, { userSub, imo }],
    queryFn: voyagesController,
    refetchOnWindowFocus: false,
    enabled: !!userSub && !!imo,
    useErrorBoundary: true,
  });

  /**
   * Handles the click event for a voyage item, changing the route to that specific voyage.
   *
   * @function handleClick
   * @param {Voyage} voyage - The voyage item that was clicked.
   * @returns {void}
   */
  const handleClick = (voyage: Voyage) => {
    if (selectedVoyageId === voyage.voyageId) {
      router.push(`${AppPath.FLEET_PERFORMANCE_ANALYSIS}/${imo}`);
    } else {
      router.push(`${AppPath.FLEET_PERFORMANCE_ANALYSIS}/${imo}/voyage/${voyage.voyageId}`);
    }
  };

  const voyages = useMemo(() => (voyagesQuery.data as Voyage[]) ?? [], [voyagesQuery.data]);

  /**
   * Group voyages by year.
   */
  const voyagesGroupedByYear = useMemo(() => {
    const voyagesWithYear: CanGroup<Voyage>[] = voyages.map((voyage) => {
      return {
        data: voyage,
        groupBy: new Date(voyage.timeDeparture).getFullYear(),
      };
    });

    return groupBy<Voyage>(voyagesWithYear);
  }, [voyages]);

  /**
   * Check if a voyage is active. Actually gets the most recent voyage.
   *
   * @param {Voyage} voyage - The voyage to check.
   * @returns {boolean} True if the voyage is active, false otherwise.
   */
  const checkVoyageIsActive = useCallback(
    (voyage: Voyage) => {
      const years = Object.keys(voyagesGroupedByYear).reverse();

      return voyagesGroupedByYear[years[0]][0] == voyage;
    },
    [voyagesGroupedByYear]
  );

  const noVoyages = voyages.length === 0;

  return voyagesQuery.isLoading ? (
    <Box minW={72}>
      <Spinner size='xl' minH={40} />
    </Box>
  ) : (
    <Card h={height} w={{ base: 'auto', md: 'min-content' }} mx='auto' maxW='calc(100vw - 2rem)'>
      <DataStateWrapper isEmpty={noVoyages} w={{ md: 72 }}>
        {carousel ? (
          <Flex direction='column' h='full' pb={2.5}>
            <VoyageCarousel
              key={imo}
              voyagesGroupedByYear={voyagesGroupedByYear}
              selectedVoyageId={selectedVoyageId}
              handleClick={handleClick}
              checkVoyageIsActive={checkVoyageIsActive}
            />
          </Flex>
        ) : (
          <Flex direction='column' h='full' pb={2.5} data-testid='fleet-performance-voyage-list'>
            <Text as='h3' textAlign='left' fontSize='xl' p={3} fontWeight='medium'>
              {text.voyage_list.voyages}
            </Text>
            <VStackWithScroll
              overflowY='auto'
              border='section'
              borderStyle='none'
              borderTopStyle='solid'
              overflowX='hidden'
              spacing={0}
              w={71}
            >
              {Object.entries(voyagesGroupedByYear)
                .reverse()
                .map(([year, voyages], index) => (
                  <Box key={year} w='full'>
                    <Box
                      bg='backgrounds.default'
                      textAlign='center'
                      fontSize='xs'
                      border='section'
                      borderStyle='none'
                      borderTopStyle={index > 0 ? 'solid' : 'none'}
                      borderBottomStyle='solid'
                      position='sticky'
                      zIndex={2}
                      // ?NOTE: top and pt avoid the flickering when scrolling
                      top='-1px'
                      pt='1px'
                    >
                      {year}
                    </Box>
                    <VoyageInnerList
                      voyages={voyages}
                      handleClick={handleClick}
                      selectedVoyageId={selectedVoyageId}
                      checkVoyageIsActive={checkVoyageIsActive}
                    />
                  </Box>
                ))}
            </VStackWithScroll>
          </Flex>
        )}
      </DataStateWrapper>
    </Card>
  );
};
